// File: src/components/friends/ContactPage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as NoMatchesSVG } from "../../assets/NoMatches.svg";
import { IoSearchSharp } from "react-icons/io5";
import ProfileActionSheet from "../ProfileActionSheet";
import MinimalFooter from "../MinimalFooter";

function Icon({ name, style = {} }) {
  return <i className={`fas fa-${name}`} style={style} aria-hidden="true" />;
}

// Helper: Calculate Age
function calculateAge(u) {
  if (!u?.dateOfBirth) return "";
  const dob = new Date(u.dateOfBirth);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

export default function ContactPage({
  accepted,
  currentUserId,
  onMessage,
  onUserBlocked,
}) {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileModalUser, setProfileModalUser] = useState(null);

  // Local search term
  const [searchTerm, setSearchTerm] = useState("");

  // For auto-focusing a contact request from query params
  const location = useLocation();
  const navigate = useNavigate();
  const [focusedRequestId, setFocusedRequestId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusId = params.get("focusRequestId");
    setFocusedRequestId(focusId);
  }, [location.search]);

  function handleUnfocus() {
    const newParams = new URLSearchParams(location.search);
    newParams.delete("focusRequestId");
    navigate({ pathname: location.pathname, search: newParams.toString() }, { replace: true });
  }

  async function openProfileModal(user) {
  try {
    const resp = await axios.get(
      `${process.env.REACT_APP_API_URL}/users/${user._id}`
    );

    const fullUser = resp.data;

    let isHost = false;

    if (fullUser?.email) {
      try {
        const hostResp = await axios.get(
          `${process.env.REACT_APP_API_URL}/promoters/is-host`,
          {
            params: {
              email: fullUser.email,
            },
          }
        );

        isHost = hostResp.data?.isHost === true;
      } catch {}
    }

    setProfileModalUser({
      ...fullUser,
      isHost,
    });

    setIsProfileModalOpen(true);
  } catch (err) {
    console.error("[ContactPage] Failed to load profile:", err);
  }
}
  function closeProfileModal() {
    setProfileModalUser(null);
    setIsProfileModalOpen(false);
  }

  function getProfilePicUrl(userObj) {
    if (!userObj?.profilePicUrl) return "/default-avatar.png";
    return userObj.profilePicUrl;
  }

  // 1) Ghost state if no accepted contacts - includes the search bar container
  if (!accepted || accepted.length === 0) {
    return (
      <div style={pageShell}>
        <div style={contentFlex}>
          <div style={styles.pageWrapper}>
            {/* SEARCH BAR */}
            <div style={styles.searchBarContainer}>
              <div style={styles.searchBar}>
                <IoSearchSharp style={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search..."
                  style={styles.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div style={ghostStyles.wrapper}>
              <div style={ghostStyles.svgWrapper}>
                <NoMatchesSVG width={220} />
              </div>
              <div style={ghostStyles.title}>No Contacts</div>
              <div style={ghostStyles.subtitle}>
                You haven’t accepted any connections. Keep exploring!
              </div>
            </div>
          </div>
        </div>
        <MinimalFooter />
      </div>
    );
  }

  // 2) Sort accepted contacts alphabetically by full name
  const sortedAccepted = [...accepted].sort((a, b) => {
    const iAmSenderA =
      String(a.req.fromUserId?._id || a.req.fromUserId) === String(currentUserId);
    const otherUserA = iAmSenderA ? a.req.toUserId : a.req.fromUserId;
    const fullNameA = `${otherUserA.firstName || ""} ${otherUserA.lastName || ""}`
      .trim()
      .toLowerCase();

    const iAmSenderB =
      String(b.req.fromUserId?._id || b.req.fromUserId) === String(currentUserId);
    const otherUserB = iAmSenderB ? b.req.toUserId : b.req.fromUserId;
    const fullNameB = `${otherUserB.firstName || ""} ${otherUserB.lastName || ""}`
      .trim()
      .toLowerCase();

    return fullNameA.localeCompare(fullNameB);
  });

  // 3) Filter by searchTerm
  const filteredAccepted = !searchTerm
    ? sortedAccepted
    : sortedAccepted.filter((item) => {
        const iAmSender =
          String(item.req.fromUserId?._id || item.req.fromUserId) === String(currentUserId);
        const otherUser = iAmSender ? item.req.toUserId : item.req.fromUserId;
        const fullName = `${otherUser.firstName || ""} ${otherUser.lastName || ""}`
          .trim()
          .toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
      });

  // 4) Group by first letter of FIRST name, after sorting
  const groupedContacts = filteredAccepted.reduce((acc, item) => {
    const iAmSender =
      String(item.req.fromUserId?._id || item.req.fromUserId) === String(currentUserId);
    const otherUser = iAmSender ? item.req.toUserId : item.req.fromUserId;
    const firstLetter = (otherUser.firstName || "").charAt(0).toUpperCase() || "#";
    if (!acc[firstLetter]) acc[firstLetter] = [];
    acc[firstLetter].push(item);
    return acc;
  }, {});
  const letters = Object.keys(groupedContacts).sort();

  return (
    <div style={pageShell}>
      <div style={contentFlex}>
        <div style={styles.pageWrapper}>
          {/* Overlay for focused contact */}
          {focusedRequestId && <div onClick={handleUnfocus} style={styles.overlay} />}

          {/* SEARCH BAR */}
          <div style={styles.searchBarContainer}>
            <div style={styles.searchBar}>
              <IoSearchSharp style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* If no results => simple "no matches" message */}
          {filteredAccepted.length === 0 && (
            <div style={styles.noMatches}>
              <p>No matching contacts found.</p>
            </div>
          )}

          {/* 5) Render grouped list by letter */}
          <div style={styles.contactsWrapper}>
            {letters.map((letter) => (
              <div key={letter} style={styles.letterGroup}>
                <div style={styles.letterHeader}>
                  <span style={styles.letterPill}>{letter}</span>
                </div>

                {groupedContacts[letter].map(({ req }) => {
                  const iAmSender =
                    String(req.fromUserId?._id || req.fromUserId) === String(currentUserId);
                  const otherUser = iAmSender ? req.toUserId : req.fromUserId;

                  const fullName = `${otherUser.firstName || "Unknown"}${
                    otherUser.lastName ? " " + otherUser.lastName.charAt(0) + "." : ""
                  }`.trim();
                  const age = calculateAge(otherUser);
                  const gender = otherUser.gender || "";
                  const subtitle = age ? `${age} • ${gender}` : gender;

                  const isFocused = String(req._id) === focusedRequestId;

                  return (
                    <div
                      key={req._id}
                      style={{
                        ...styles.contactItem,
                        ...(isFocused ? styles.focusedStyle : {}),
                      }}
                    >
                      <img
                        src={getProfilePicUrl(otherUser)}
                        alt="avatar"
                        style={styles.avatar}
                      />
                      <div style={styles.contactInfo}>
                        <p style={styles.contactName}>{fullName}</p>
                        {subtitle && <p style={styles.contactDetails}>{subtitle}</p>}
                      </div>
                      <div style={styles.contactActions}>
                        <button
                          style={styles.msgBtn}
                          onClick={() => onMessage && onMessage(otherUser._id)}
                        >
                          <Icon name="comment" style={{ marginRight: 6 }} />
                          Chat
                        </button>
                        <button
                          style={styles.profileBtn}
                          onClick={() => openProfileModal(otherUser)}
                        >
                          <Icon name="eye" style={{ marginRight: 6 }} />
                          View
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Profile ACTION SHEET */}
          {isProfileModalOpen && profileModalUser && (
            <ProfileActionSheet
  open={isProfileModalOpen}
  onClose={closeProfileModal}
  user={profileModalUser}
  isHost={profileModalUser?.isHost === true}
  onUserBlocked={onUserBlocked}
/>
          )}
        </div>
      </div>
      <MinimalFooter />
    </div>
  );
}

const styles = {
  pageWrapper: {
    width: "100%",
    maxWidth: "900px",
    margin: "0 auto",
    padding: "12px 16px 24px",
    boxSizing: "border-box",
    position: "relative",
    fontFamily:
      "Open Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
  },

  /* Search Bar */
  searchBarContainer: { 
    marginBottom: 16,
    marginTop: 0,
  },
  searchBar: { position: "relative", width: "100%" },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 16,
    color: "#999",
  },
  searchInput: {
    width: "100%",
    padding: "10px 36px",
    borderRadius: 10,
    border: "1px solid #E7E9EE",
    backgroundColor: "#FAFAFA",
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
  },

  noMatches: {
    textAlign: "center",
    color: "#999",
    marginTop: 32,
    fontSize: 13,
  },

  /* Contacts List */
  contactsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  letterGroup: {},
  letterHeader: {
    margin: "8px 0",
    display: "flex",
    alignItems: "center",
  },
  letterPill: {
    background: "#F7F8FA",
    border: "1px solid #EEF1F6",
    color: "#3A4050",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
  },

  /* Contact Card */
  contactItem: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #E7E9EE",
    borderRadius: 12,
    padding: 12,
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "#fff",
    transition: "box-shadow 0.2s, transform 0.2s",
    marginBottom: 8,
  },
  avatar: {
    width: 45,
    height: 45,
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: 12,
    cursor: "pointer",
    background: "#F3F4F6",
    border: "2px solid #E7E9EE",
  },
  contactInfo: { flex: 1, minWidth: 0 },
  contactName: {
    margin: 0,
    fontSize: 15,
    fontWeight: 900,
    color: "#000",
    letterSpacing: "-0.01em",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  contactDetails: {
    margin: 0,
    marginTop: 2,
    fontSize: 12,
    color: "#6B7280",
  },

  contactActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: 8,
    flexShrink: 0,
  },
  msgBtn: {
    backgroundColor: "#007BFF",
    color: "#fff",
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid #007BFF",
    borderRadius: 10,
    padding: "8px 16px",
    cursor: "pointer",
    lineHeight: 1,
    paddingRight: 24,
  },
  profileBtn: {
    backgroundColor: "#fff",
    color: "#111",
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid #E7E9EE",
    borderRadius: 10,
    padding: "8px 16px",
    cursor: "pointer",
    lineHeight: 1,
    paddingRight: 24,
  },

  /* Focused (via query param) */
  focusedStyle: {
    position: "relative",
    zIndex: 100,
    transform: "scale(1.02)",
    border: "1px solid #4285F4",
    boxShadow: "0 0 12px rgba(66,133,244,0.25)",
  },
};

const ghostStyles = {
  wrapper: {
    textAlign: "center",
    marginTop: "25%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  svgWrapper: { borderRadius: "50%", marginBottom: "1rem" },
  title: {
    fontSize: "1rem",
    fontWeight: 700,
    marginBottom: "0.35rem",
    color: "#0f172a",       // dark neutral for consistency
    letterSpacing: "-0.01em",
  },
  subtitle: {
    fontSize: ".88rem",
    color: "#6b7280",       // subtle gray like other subheadings
    maxWidth: "240px",
    lineHeight: "1.45",
    margin: "0 auto",       // keep centered under title
  },  
};

/* === Sticky footer wrappers (same pattern as ProfilePage) === */
const pageShell = {
  display: "flex",
  flexDirection: "column",
  minHeight: "80vh",
  background: "#FFFFFF",
};
const contentFlex = {
  flex: 1, // lets content grow and pushes footer to the bottom
};
